import { useEffect, useRef, useState } from 'react'
import { ChangeValue, FormattedData, Periods } from 'pages/account/wallets/info/wallet.types'
import { fetchRates } from 'store/reducers/rates'
import { MOCK_USD_WALLET } from 'pages/account/wallets/info/wallet.mock'
import { ICurrency } from 'store/fetchers/currencies'
import {
  calculateChange,
  calculateStartDate,
  formatData,
  getAggregate,
  getIntervals,
  getLimit,
} from 'pages/account/wallets/info/wallet.utils'
import { useAppDispatch } from 'store/hooks'
import { MONTH, THREE_DAYS, WEEK, YEAR } from 'pages/account/wallets/info/wallet.constants'

interface UseAddRateData {
  currency: ICurrency
  period: Periods
  addTimeToFormatter?: boolean
  isDashboard?: boolean
}

export const useRateInfo = ({
  currency,
  period,
  addTimeToFormatter,
  isDashboard,
}: UseAddRateData): { actualRate: number; changeValue: ChangeValue; graphData: FormattedData[] } => {
  const dispatch = useAppDispatch()

  const [graphData, setGraphData] = useState<FormattedData[]>([])
  const [changeValue, setChangeValue] = useState<ChangeValue>()
  const [actualRate, setActualRate] = useState<number>()
  const isLoaded = useRef<boolean>(false)

  const previousPeriod = useRef(period)

  useEffect(() => {
    let cancelled = false

    // Если period поменялся — сбросить флаг загрузки
    if (previousPeriod.current !== period) {
      isLoaded.current = false
      previousPeriod.current = period
    }

    async function loadRates() {
      if (!currency || isLoaded.current) return

      const fetchedRates = await dispatch(
        fetchRates({
          base: currency,
          target: (MOCK_USD_WALLET as unknown) as ICurrency,
          from: calculateStartDate(period),
          minute: period !== MONTH && period !== YEAR && period !== WEEK && period !== THREE_DAYS,
          limit: getLimit(period),
          aggregate: getAggregate(period),
          intervals: getIntervals(period),
          isDashboard,
        }),
      )

      if (!cancelled) {
        setGraphData(formatData(period, fetchedRates, addTimeToFormatter))
        isLoaded.current = true
      }
    }

    loadRates()

    return () => {
      cancelled = true
    }
  }, [currency, period])

  useEffect(
    function updateActualRateAndChange() {
      if (graphData.length) {
        setActualRate(graphData[graphData.length - 1].rate)
        setChangeValue(calculateChange(graphData))
      }
    },
    [graphData],
  )

  return {
    changeValue,
    actualRate,
    graphData,
  }
}
